import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ApiError, api } from '@/services/api/client';
import { queryKeys } from '@/services/queries/keys';
import { Skeleton } from '@/shared/ui/skeleton';
import { cn } from '@/lib/utils';
import type { Issue } from '@/types';

/**
 * The conversation on one issue: NuDent support's replies and the lab's own.
 *
 * Support answers from the ops portal; each reply also reaches whoever raised
 * the issue by email. A reply here on a resolved or waiting issue puts it back
 * in support's queue. A closed issue takes no replies - raise a new one.
 *
 * Endpoints and keys stay in this file: nothing else reads the thread. The key
 * sits under `issues`, so anything that refreshes the issue list refreshes the
 * thread with it.
 */

interface Comment {
  id: string;
  author: 'staff' | 'customer';
  authorName: string;
  body: string;
  createdAt: string;
}

const REPLY_MAX = 5000;
const threadKey = (issueId: string) => [...queryKeys.issues.all, 'comments', issueId] as const;
const path = (issueId: string) => `/issues/${issueId}/comments`;

/** "19 Aug 2026, 14:05" - the queue writes days; a conversation needs the time too. */
function when(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function initials(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('') || '?'
  );
}

export function IssueThread({ issue }: { issue: Issue }) {
  const queryClient = useQueryClient();
  const [text, setText] = useState('');

  const thread = useQuery({
    queryKey: threadKey(issue.id),
    queryFn: ({ signal }) => api.get<Comment[]>(path(issue.id), { signal }),
    staleTime: 30_000,
    // Support answers whenever they get to it, and nothing pushes to the
    // browser - so an open issue checks for a reply each minute.
    refetchInterval: issue.status === 'closed' ? false : 60_000,
  });

  const reply = useMutation({
    mutationFn: (body: string) => api.post<Comment>(path(issue.id), { body }),
    onSuccess: async () => {
      setText('');
      // The status may have moved (resolved -> open), so the list refreshes too.
      await queryClient.invalidateQueries({ queryKey: queryKeys.issues.all });
      toast.success('Reply sent to support');
    },
    onError: (err) => {
      toast.error(
        err instanceof ApiError && err.status === 409
          ? 'This issue is closed. Raise a new one if you still need help.'
          : 'Your reply could not be sent. Try again in a moment.',
      );
    },
  });

  const closed = issue.status === 'closed';
  const comments = thread.data ?? [];

  return (
    <div className="flex flex-col gap-4">
      {thread.isPending && <Skeleton className="h-16 w-full rounded-[10px]" />}

      {thread.isSuccess && comments.length === 0 && !closed && (
        <p className="text-[12.5px] text-muted-foreground">
          No replies yet. Support answers here and by email.
        </p>
      )}

      {comments.map((comment) => {
        const staff = comment.author === 'staff';
        return (
          <div key={comment.id} className="flex gap-3">
            <span
              className={cn(
                'flex size-[30px] flex-none items-center justify-center rounded-[9px] text-[11px] font-semibold',
                staff ? 'bg-primary text-primary-foreground' : 'bg-brand-50 text-brand-600',
              )}
              aria-hidden="true"
            >
              {staff ? 'NU' : initials(comment.authorName)}
            </span>
            <div className="min-w-0 flex-1">
              <div className="mb-1.5 flex flex-wrap items-baseline gap-2.5">
                <span className="text-[12.5px] font-semibold">
                  {staff ? `${comment.authorName} · NuDent support` : comment.authorName}
                </span>
                <span className="text-[10.5px] text-muted-foreground">
                  {when(comment.createdAt)}
                </span>
              </div>
              <div
                className={cn(
                  'rounded-[10px] border px-3.5 py-3 text-[12.5px] leading-[1.6] whitespace-pre-wrap',
                  staff ? 'border-brand-200 bg-brand-50/60' : 'border-border bg-muted/40',
                )}
              >
                {comment.body}
              </div>
            </div>
          </div>
        );
      })}

      {thread.isError && (
        <p className="text-[12.5px] text-muted-foreground">
          Replies could not be loaded just now. Try again shortly.
        </p>
      )}

      {issue.status === 'waiting_on_user' && (
        <p className="rounded-[10px] border border-pill-caution-foreground/20 bg-pill-caution px-3.5 py-2.5 text-[12px] font-medium text-pill-caution-foreground">
          Support is waiting for your reply.
        </p>
      )}

      {!closed && (
        <form
          className="flex flex-col gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            const body = text.trim();
            if (body) reply.mutate(body);
          }}
        >
          <label htmlFor={`reply-${issue.id}`} className="sr-only">
            Reply to support
          </label>
          <textarea
            id={`reply-${issue.id}`}
            rows={3}
            value={text}
            maxLength={REPLY_MAX}
            onChange={(event) => {
              setText(event.target.value);
            }}
            placeholder="Write a reply…"
            className="w-full rounded-[10px] border border-input bg-transparent px-3.5 py-2.5 text-[12.5px] leading-[1.6] outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
          />
          <button
            type="submit"
            disabled={reply.isPending || !text.trim()}
            className="h-9 w-fit self-end rounded-lg bg-primary px-4 text-[12.5px] font-semibold text-primary-foreground disabled:opacity-60"
          >
            Send reply
          </button>
        </form>
      )}
    </div>
  );
}
